import React from 'react';
import { connect } from 'react-redux';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { WindowContext } from '@ff-os/shared-react/context/window/WindowContext';
import { Prefab } from '@ff-resource-plugins/prefabs-shared/Prefab';
import { selectNonPinnedVariables, selectPinnedVariables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { noop } from 'lodash';
import { selectActivePrefab } from '../../_redux-prefabs';
import { useOrientation } from '../../context/Orientation';
import { usePreviewSize } from '../../context/PreviewSize';
import { VariableInputControlSlot } from '../../VariableInputControlSlot';
import { ContentFirstGiftWindowStepProps } from '../shared/giftWindowSteps';
import { GiftWindowFooter } from '../shared/partials/GiftWindowFooter';
import { GiftWindowPreviewUi } from '../shared/partials/GiftWindowPreviewUi';
import { ContentFirstHeader } from './partials/ContentFirstHeader';
import { ExpandableDescription } from './partials/ExpandableDescription';
import { ImageSlider } from './partials/ImageSlider';
import styles from './ContentFirstDesignWindow.module.scss';

const _ContentFirstDesignWindow = ({
    onClose,
    pinnedVariables,
    nonPinnedVariables,
    activePrefab
}: Props) => {
    useOrientation('landscape');
    usePreviewSize('hidden');

    const sliderImages = React.useMemo(() => {
        const images: Array<{
            url?: string;
            alt?: string;
            isLivePreview?: boolean;
            renderPreview?: () => React.ReactNode;
        }> = [];

        // Add preview images from the prefab
        if (activePrefab?.previewImagesUrls && activePrefab.previewImagesUrls.length > 0) {
            activePrefab.previewImagesUrls.forEach((url, index) => {
                images.push({
                    url,
                    alt: `${activePrefab.title || 'Design'} preview ${index + 1}`
                });
            });
        }

        images.push({
            isLivePreview: true,
            alt: 'Live preview',
            renderPreview: () => <GiftWindowPreviewUi isFullscreen={false} toggleFullscreen={noop} hideFullscreenButton={true} />
        });

        return images;
    }, [activePrefab?.previewImagesUrls, activePrefab?.title]);

    return (
        <WindowContext isOpen close={noop} deps={[activePrefab?.id, activePrefab?.title, activePrefab?.description]}>
            <WindowContext.Header>
                <ContentFirstHeader previous={undefined} onClose={onClose} />
            </WindowContext.Header>
            <WindowContext.Body>
                <Grid direction='row' gap='l' className={styles.contentFirst}>
                    <Cell className={styles.imageSection}>
                        <ImageSlider images={sliderImages} />
                    </Cell>

                    <Cell className={styles.controlsSection}>
                        <Grid direction='column' gap='m' className={styles.controlsContainer}>
                            <Cell className={styles.headerSection}>
                                {activePrefab?.title && (
                                    <h2 className={styles.title}>{activePrefab.title}</h2>
                                )}
                                {activePrefab?.description && (
                                    <ExpandableDescription description={activePrefab.description} />
                                )}
                            </Cell>

                            <Cell className={styles.variablesSection}>
                                <Grid direction='column' gap='m'>
                                    {pinnedVariables.length > 0 && (
                                        <Cell>
                                            <Grid direction='column' gap='s'>
                                                {pinnedVariables.map(({ id }) => (
                                                    <VariableInputControlSlot variableIds={[id]}
                                                        render={Control => (
                                                            <Cell key={id}>
                                                                <Control />
                                                            </Cell>
                                                        )} />
                                                ))}
                                            </Grid>
                                        </Cell>
                                    )}

                                    {nonPinnedVariables.length > 0 && (
                                        <Cell>
                                            <Grid direction='column' gap='s'>
                                                {nonPinnedVariables.map(({ id }) => (
                                                    <VariableInputControlSlot variableIds={[id]}
                                                        render={Control => (
                                                            <Cell key={id}>
                                                                <Control />
                                                            </Cell>
                                                        )} />
                                                ))}
                                            </Grid>
                                        </Cell>
                                    )}
                                </Grid>
                            </Cell>
                        </Grid>
                    </Cell>
                </Grid>
            </WindowContext.Body>
            <WindowContext.Footer>
                <GiftWindowFooter
                    next={undefined} nextButtonText={undefined}
                    previous={undefined} previousButtonText={undefined}
                    showPrevious={false}
                    onClose={onClose}
                    contentFirst={true} />
            </WindowContext.Footer>
        </WindowContext>
    );
};

type Props = ContentFirstGiftWindowStepProps & {
    pinnedVariables: GiftJson.Variable<any>[];
    nonPinnedVariables: GiftJson.Variable<any>[];
    activePrefab: Prefab | undefined;
};

const mapStateToProps = (state: GiftPdpState) => ({
    pinnedVariables: selectPinnedVariables(state),
    nonPinnedVariables: selectNonPinnedVariables(state),
    activePrefab: selectActivePrefab(state)
});

export const ContentFirstDesignWindow = connect(
    mapStateToProps
)(_ContentFirstDesignWindow);